import { CoachPortalSession } from '@airreading/common/data';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { InAppAssessmentWindow } from './in-app-assessment-window';

interface InAppAssessmentContextValue {
  openSessionId: string | null;
  open: (session: CoachPortalSession) => void;
  close: () => void;
}

const InAppAssessmentContext = createContext<InAppAssessmentContextValue | null>(null);

/**
 * Keeps a single assessment window mounted across `/my-classes` tabs.
 *
 * Triggers live in short-lived surfaces — a calendar popover closes the moment you click inside it —
 * so they hand the session to this provider instead of rendering the window themselves.
 */
export function InAppAssessmentProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CoachPortalSession | null>(null);

  const open = useCallback((next: CoachPortalSession) => setSession(next), []);
  const close = useCallback(() => setSession(null), []);

  const value = useMemo(() => ({ openSessionId: session?.id ?? null, open, close }), [session?.id, open, close]);

  return (
    <InAppAssessmentContext.Provider value={value}>
      {children}
      {/* Keyed so switching classes starts a fresh run rather than inheriting the previous timers. */}
      {session && <InAppAssessmentWindow key={session.id} session={session} open onClose={close} />}
    </InAppAssessmentContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInAppAssessment(): InAppAssessmentContextValue {
  const context = useContext(InAppAssessmentContext);
  if (!context) {
    throw new Error('useInAppAssessment must be used inside an InAppAssessmentProvider');
  }
  return context;
}
